import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import * as api from '../../services/api';
import { UserSelectors } from '../../reducers/user';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 32px;
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeIn} 0.22s ease;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1a2744;
  margin: 0 0 4px;
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  margin: 0 0 28px;
`;

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 2px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid ${(p) => (p.$active ? '#3b82f6' : 'transparent')};
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background: ${(p) => (p.$active ? '#eff6ff' : 'transparent')};
  color: ${(p) => (p.$active ? '#1d4ed8' : '#64748b')};
  cursor: pointer;
  &:hover { color: #1e40af; background: ${(p) => (p.$active ? '#eff6ff' : '#f8fafc')}; }
`;

// ─── Category filter chips ────────────────────────────────────────────────────

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const Chip = styled.button<{ $active: boolean }>`
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 500;
  border: 1.5px solid ${(p) => (p.$active ? '#2563eb' : '#e5e7eb')};
  background: ${(p) => (p.$active ? '#eff6ff' : '#fff')};
  color: ${(p) => (p.$active ? '#1d4ed8' : '#6b7280')};
  cursor: pointer;
  &:hover { border-color: #2563eb; color: #1d4ed8; }
`;

// ─── Exercise grid ────────────────────────────────────────────────────────────

const ExerciseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const ExerciseCard = styled.div`
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
`;

const ExerciseName = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1a2744;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ $color?: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) => p.$color ?? '#f1f5f9'};
  color: ${(p) => (p.$color ? '#fff' : '#64748b')};
`;

const ExerciseDesc = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FreqDose = styled.div`
  font-size: 0.8125rem;
  color: #374151;
  font-style: italic;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const BtnPrimary = styled.button`
  padding: 7px 14px;
  background: #2563eb;
  color: #fff;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #1d4ed8; }
`;

const BtnSecondary = styled.button`
  padding: 7px 14px;
  background: #f1f5f9;
  color: #374151;
  border-radius: 7px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #e2e8f0; }
`;

const BtnDanger = styled.button`
  padding: 5px 10px;
  background: #fef2f2;
  color: #dc2626;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #fee2e2; }
`;

// ─── Patient picker ───────────────────────────────────────────────────────────

const PatientPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1a2744;
  min-width: 220px;
  background: #fff;
  &:focus { outline: none; border-color: #2563eb; }
`;

// ─── Assignment list ──────────────────────────────────────────────────────────

const AssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const AssignmentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`;

const AssignmentInfo = styled.div`
  flex: 1;
`;

const AssignmentTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: #1a2744;
`;

const AssignmentMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 2px;
`;


const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
`;

const MetricCell = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
`;

const MetricLabel = styled.div`
  font-size: 0.6875rem;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
`;

const MetricValue = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1a2744;
`;

// ─── Empty state ──────────────────────────────────────────────────────────────

const Empty = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9375rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1.5px dashed #e5e7eb;
`;

// ─── Video modal ──────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0,0,0,0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const VideoBox = styled.div`
  background: #000;
  border-radius: 14px;
  overflow: hidden;
  max-width: 900px;
  width: 92vw;
  box-shadow: 0 24px 64px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
`;

const VideoHeader = styled.div`
  padding: 14px 18px;
  background: #111827;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const VideoTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #f9fafb;
`;

const VideoNote = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 2px;
`;

const CloseBtn = styled.button`
  font-size: 22px;
  color: #9ca3af;
  background: none;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  &:hover { color: #f9fafb; }
`;

// ─── Assign modal ─────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  max-width: 640px;
  width: 92vw;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;

const ModalHead = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #1a2744;
`;

const ModalBody = styled.div`
  padding: 20px 22px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 14px 22px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  &:focus { outline: none; border-color: #2563eb; }
  box-sizing: border-box;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 72px;
  &:focus { outline: none; border-color: #2563eb; }
  box-sizing: border-box;
`;

const FieldLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 5px;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const UploadZone = styled.div<{ $over?: boolean }>`
  border: 2px dashed ${(p) => (p.$over ? '#2563eb' : '#d1d5db')};
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  background: ${(p) => (p.$over ? '#eff6ff' : '#fafafa')};
  color: ${(p) => (p.$over ? '#1d4ed8' : '#9ca3af')};
  font-size: 0.875rem;
  transition: border-color 0.12s, background 0.12s;
  &:hover { border-color: #2563eb; color: #1d4ed8; background: #f0f7ff; }
`;

const ErrMsg = styled.div`
  color: #dc2626;
  font-size: 0.8125rem;
  margin-top: 8px;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORIES = ['vision', 'balance', 'proprioception', 'cognitive', 'jaw', 'ocular', 'general'];

const CATEGORY_COLORS: Record<string, string> = {
  vision: '#0369a1',
  balance: '#059669',
  proprioception: '#7c3aed',
  cognitive: '#b45309',
  jaw: '#dc2626',
  ocular: '#0891b2',
  general: '#64748b',
};

const fmtDate = (d: string) => {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const fmtMetricKey = (k: string) => k.replace(/_avg$/, '').replace(/_/g, ' ');

// ─── Video Modal ──────────────────────────────────────────────────────────────

interface VideoModalProps {
  exercise: api.HomeworkExercise;
  note?: string;
  onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ exercise, note, onClose }) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(exercise.video_url ?? null);
  const [loading, setLoading] = useState(!exercise.video_url && exercise.has_video);

  useEffect(() => {
    if (exercise.video_url || !exercise.has_video) return;
    setLoading(true);
    api.getHomeworkExerciseDetail(exercise.id)
      .then((ex) => setVideoUrl(ex.video_url ?? null))
      .catch(() => setVideoUrl(null))
      .finally(() => setLoading(false));
  }, [exercise.id, exercise.video_url, exercise.has_video]);

  return (
    <Overlay onClick={onClose}>
      <VideoBox onClick={(e) => e.stopPropagation()}>
        <VideoHeader>
          <div>
            <VideoTitle>{exercise.name}</VideoTitle>
            {note && <VideoNote>{note}</VideoNote>}
          </div>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </VideoHeader>
        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#000' }}>Loading video…</div>
        )}
        {!loading && videoUrl && (
          <video
            src={videoUrl}
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '72vh', background: '#000' }}
          />
        )}
        {!loading && !videoUrl && (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', background: '#1f2937', fontSize: 14 }}>
            Video not available yet.
          </div>
        )}
      </VideoBox>
    </Overlay>
  );
};

// ─── Assign Exercise Modal (clinician) ────────────────────────────────────────

interface AssignModalProps {
  exercises: api.HomeworkExercise[];
  patientName: string;
  onClose: () => void;
  onAssigned: (a: api.HomeworkAssignment) => void;
  patientId: number;
}

const AssignModal: React.FC<AssignModalProps> = ({ exercises, patientName, patientId, onClose, onAssigned }) => {
  const [selected, setSelected] = useState('');
  const [notes, setNotes] = useState('');
  const [reps, setReps] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const visible = filterCat ? exercises.filter((e) => e.category === filterCat) : exercises;

  const handleAssign = async () => {
    if (!selected) { setErr('Select an exercise.'); return; }
    setSaving(true); setErr('');
    try {
      const a = await api.createHomeworkAssignment(patientId, selected, notes, reps);
      onAssigned(a);
    } catch (e: any) {
      setErr(e?.data?.error ?? e?.message ?? 'Failed to assign.');
    } finally { setSaving(false); }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <ModalTitle>Assign Exercise to {patientName}</ModalTitle>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </ModalHead>
        <ModalBody>
          <ChipRow style={{ marginBottom: 14 }}>
            <Chip $active={filterCat === ''} onClick={() => setFilterCat('')}>All</Chip>
            {CATEGORIES.map((c) => (
              <Chip key={c} $active={filterCat === c} onClick={() => setFilterCat(c)}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </Chip>
            ))}
          </ChipRow>

          <FieldGroup>
            <FieldLabel>Exercise</FieldLabel>
            <Select value={selected} onChange={(e) => setSelected(e.target.value)} style={{ width: '100%' }}>
              <option value="">— select an exercise —</option>
              {visible.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name} ({ex.category})
                </option>
              ))}
            </Select>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Repetitions / Dosage override <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></FieldLabel>
            <Input
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              placeholder="e.g. 3 sets of 10 reps, twice daily"
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Clinical note <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Focus on slow controlled movement first"
            />
          </FieldGroup>

          {err && <ErrMsg>{err}</ErrMsg>}
        </ModalBody>
        <ModalFooter>
          <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
          <BtnPrimary onClick={handleAssign} disabled={saving}>
            {saving ? 'Assigning…' : 'Assign'}
          </BtnPrimary>
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
};

// ─── Patient view ─────────────────────────────────────────────────────────────

const PatientView: React.FC = () => {
  const [assignments, setAssignments] = useState<api.HomeworkAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [videoFor, setVideoFor] = useState<api.HomeworkAssignment | null>(null);

  useEffect(() => {
    api.getMyHomework().catch(() => [])
      .then((hw) => setAssignments(Array.isArray(hw) ? hw : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Empty>Loading…</Empty>;

  return (
    <>
      {assignments.length === 0 ? (
        <Empty>No exercises assigned yet. Your clinician will assign exercises after your next session.</Empty>
      ) : (
        <ExerciseGrid>
          {assignments.map((a) => (
            <ExerciseCard key={a.id}>
              <BadgeRow>
                <Badge $color={CATEGORY_COLORS[a.exercise.category]}>{a.exercise.category}</Badge>
                {'★'.repeat(a.exercise.difficulty_level)}
              </BadgeRow>
              <ExerciseName>{a.exercise.name}</ExerciseName>
              {a.exercise.frequency_dose && <FreqDose>{a.repetitions || a.exercise.frequency_dose}</FreqDose>}
              {a.exercise.instructions && <ExerciseDesc>{a.exercise.instructions}</ExerciseDesc>}
              {a.notes && (
                <div style={{ fontSize: '0.75rem', color: '#4b5563', fontStyle: 'italic', borderLeft: '3px solid #d1d5db', paddingLeft: 8 }}>
                  {a.notes}
                </div>
              )}
              {a.exercise.has_video && (
                <CardActions>
                  <BtnPrimary onClick={() => setVideoFor(a)}>▶ Demo Video</BtnPrimary>
                </CardActions>
              )}
            </ExerciseCard>
          ))}
        </ExerciseGrid>
      )}

      {videoFor && (
        <VideoModal
          exercise={videoFor.exercise}
          note={videoFor.notes || undefined}
          onClose={() => setVideoFor(null)}
        />
      )}
    </>
  );
};

// ─── Clinician view ───────────────────────────────────────────────────────────

type ClinicianTab = 'catalog' | 'assign';

const ClinicianView: React.FC = () => {
  const [tab, setTab] = useState<ClinicianTab>('catalog');
  const [exercises, setExercises] = useState<api.HomeworkExercise[]>([]);
  const [filterCat, setFilterCat] = useState('');
  const [patients, setPatients] = useState<api.HomeworkPatient[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<number | ''>('');
  const [assignments, setAssignments] = useState<api.HomeworkAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignLoading, setAssignLoading] = useState(false);
  const [videoFor, setVideoFor] = useState<api.HomeworkExercise | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.getHomeworkExercises().catch(() => [] as api.HomeworkExercise[]),
      api.getHomeworkPortalPatients().catch(() => ({ patients: [] as api.HomeworkPatient[] })),
    ]).then(([exs, pRes]) => {
      setExercises(Array.isArray(exs) ? exs : []);
      setPatients(pRes?.patients ?? []);
    }).finally(() => setLoading(false));
  }, []);

  const loadAssignments = useCallback(async (patientId: number) => {
    setAssignLoading(true);
    try {
      const res = await api.getPatientHomeworkAssignments(patientId);
      setAssignments(Array.isArray(res) ? res : []);
    } catch { setAssignments([]); }
    finally { setAssignLoading(false); }
  }, []);

  const handlePatientChange = (id: number | '') => {
    setSelectedPatientId(id);
    setAssignments([]);
    if (id !== '') loadAssignments(id);
  };

  const handleRemove = async (assignmentId: string) => {
    setRemovingId(assignmentId);
    try {
      await api.deactivateHomeworkAssignment(assignmentId);
      setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
    } catch { /* ok */ }
    finally { setRemovingId(null); }
  };

  const visibleExercises = filterCat ? exercises.filter((e) => e.category === filterCat) : exercises;

  const selectedPatient = patients.find((p) => p.django_user_id === selectedPatientId);

  if (loading) return <Empty>Loading…</Empty>;

  return (
    <>
      <TabBar>
        <Tab $active={tab === 'catalog'} onClick={() => setTab('catalog')}>Exercise Catalog</Tab>
        <Tab $active={tab === 'assign'} onClick={() => setTab('assign')}>Assign to Patient</Tab>
      </TabBar>

      {tab === 'catalog' && (
        <>
          <ChipRow>
            <Chip $active={filterCat === ''} onClick={() => setFilterCat('')}>All</Chip>
            {CATEGORIES.map((c) => (
              <Chip key={c} $active={filterCat === c} onClick={() => setFilterCat(c)}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </Chip>
            ))}
          </ChipRow>
          {visibleExercises.length === 0 ? (
            <Empty>No exercises in the catalog yet.</Empty>
          ) : (
            <ExerciseGrid>
              {visibleExercises.map((ex) => (
                <ExerciseCard key={ex.id}>
                  <BadgeRow>
                    <Badge $color={CATEGORY_COLORS[ex.category]}>{ex.category}</Badge>
                    {ex.has_video && <Badge $color="#059669">Video ready</Badge>}
                    {'★'.repeat(ex.difficulty_level)}
                  </BadgeRow>
                  <ExerciseName>{ex.name}</ExerciseName>
                  {ex.frequency_dose && <FreqDose>{ex.frequency_dose}</FreqDose>}
                  {ex.description && <ExerciseDesc>{ex.description}</ExerciseDesc>}
                  {ex.tags.length > 0 && (
                    <BadgeRow>
                      {ex.tags.map((t) => <Badge key={t}>{t}</Badge>)}
                    </BadgeRow>
                  )}
                  {ex.has_video && (
                    <CardActions>
                      <BtnSecondary onClick={() => setVideoFor(ex)}>▶ Preview</BtnSecondary>
                    </CardActions>
                  )}
                </ExerciseCard>
              ))}
            </ExerciseGrid>
          )}
        </>
      )}

      {tab === 'assign' && (
        <>
          <PatientPickerRow>
            <Label htmlFor="patient-select">Patient</Label>
            <Select
              id="patient-select"
              value={selectedPatientId}
              onChange={(e) => handlePatientChange(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">— select a patient —</option>
              {patients.map((p) => (
                <option key={p.django_user_id} value={p.django_user_id}>
                  {p.last_name}, {p.first_name}
                </option>
              ))}
            </Select>
            {selectedPatientId !== '' && (
              <BtnPrimary onClick={() => setShowAssignModal(true)}>+ Assign Exercise</BtnPrimary>
            )}
          </PatientPickerRow>

          {selectedPatientId === '' && (
            <Empty>Select a patient to view and manage their homework assignments.</Empty>
          )}

          {selectedPatientId !== '' && assignLoading && <Empty>Loading assignments…</Empty>}

          {selectedPatientId !== '' && !assignLoading && (
            assignments.length === 0 ? (
              <Empty>No exercises assigned yet. Click "Assign Exercise" to add one.</Empty>
            ) : (
              <AssignmentList>
                {assignments.map((a) => (
                  <AssignmentRow key={a.id}>
                    <AssignmentInfo>
                      <AssignmentTitle>{a.exercise.name}</AssignmentTitle>
                      <BadgeRow style={{ margin: '4px 0' }}>
                        <Badge $color={CATEGORY_COLORS[a.exercise.category]}>{a.exercise.category}</Badge>
                        {a.exercise.has_video && <Badge $color="#059669">Has video</Badge>}
                      </BadgeRow>
                      {(a.repetitions || a.exercise.frequency_dose) && (
                        <AssignmentMeta>{a.repetitions || a.exercise.frequency_dose}</AssignmentMeta>
                      )}
                      {a.notes && <AssignmentMeta style={{ fontStyle: 'italic' }}>Note: {a.notes}</AssignmentMeta>}
                    </AssignmentInfo>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexShrink: 0 }}>
                      {a.exercise.has_video && (
                        <BtnSecondary style={{ fontSize: '0.75rem', padding: '5px 10px' }} onClick={() => setVideoFor(a.exercise)}>
                          ▶ Preview
                        </BtnSecondary>
                      )}
                      <BtnDanger onClick={() => handleRemove(a.id)} disabled={removingId === a.id}>
                        {removingId === a.id ? '…' : 'Remove'}
                      </BtnDanger>
                    </div>
                  </AssignmentRow>
                ))}
              </AssignmentList>
            )
          )}
        </>
      )}

      {videoFor && (
        <VideoModal exercise={videoFor} onClose={() => setVideoFor(null)} />
      )}

      {showAssignModal && selectedPatient && (
        <AssignModal
          exercises={exercises}
          patientName={`${selectedPatient.first_name} ${selectedPatient.last_name}`}
          patientId={selectedPatient.django_user_id}
          onClose={() => setShowAssignModal(false)}
          onAssigned={(a) => {
            setAssignments((prev) => {
              const exists = prev.findIndex((x) => x.id === a.id);
              if (exists >= 0) { const n = [...prev]; n[exists] = a; return n; }
              return [a, ...prev];
            });
            setShowAssignModal(false);
          }}
        />
      )}
    </>
  );
};

// ─── Page root ────────────────────────────────────────────────────────────────

const HomeworkPage: React.FC = () => {
  const isPatientSession = useSelector(UserSelectors().isPatientSession);

  return (
    <Page>
      <PageTitle>Homework</PageTitle>
      <PageSubtitle>
        {isPatientSession
          ? 'Your assigned exercises and training sessions.'
          : 'Exercise catalog, patient assignments, and training session management.'}
      </PageSubtitle>

      {isPatientSession ? <PatientView /> : <ClinicianView />}
    </Page>
  );
};

export default HomeworkPage;
